/**
 * `&debug` in the address: a small panel over the page that says what the
 * table is being told by the finger and the phone, and what it decided.
 *
 * It exists because touch and motion are the two things that cannot be tested
 * away from a real phone — an emulator sends clean events on a clock, a thumb
 * on iOS does neither — so when one of them does not work on a real device the
 * first question is what the page actually received, and a player can answer
 * it with a screenshot. Off unless asked for, and then nothing more than a
 * handful of writes into a plain object.
 */

const on = typeof location !== 'undefined' && new URLSearchParams(location.search).has('debug')

const values: Record<string, string> = {}
const counts: Record<string, number> = {}

/** Record the latest value of something worth seeing. */
export function probe(key: string, value: unknown): void {
  if (!on) return
  values[key] = typeof value === 'string' ? value : JSON.stringify(value)
}

/** Count an event. */
export function tally(key: string): void {
  if (!on) return
  counts[key] = (counts[key] ?? 0) + 1
}

/** Draw the panel and listen to every pointer and touch event on the page,
 *  before anything else can stop them. */
export function installProbe(): void {
  if (!on) return
  for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'touchstart', 'touchend', 'touchcancel']) {
    window.addEventListener(
      type,
      (e) => {
        const p = e as PointerEvent
        tally(p.pointerType ? `${type}:${p.pointerType}` : type)
        if (type === 'pointercancel') probe('last cancel', `${Math.round(performance.now())}ms`)
      },
      { capture: true, passive: true },
    )
  }
  window.addEventListener('deviceorientation', () => tally('deviceorientation'), { capture: true, passive: true })
  const panel = document.createElement('pre')
  panel.style.cssText =
    'position:fixed;z-index:99999;left:4px;top:4px;max-width:calc(100vw - 8px);margin:0;padding:6px 8px;' +
    'background:rgba(0,0,0,.78);color:#9ef59e;font:10px/1.35 ui-monospace,Menlo,monospace;white-space:pre-wrap;' +
    'pointer-events:none;border-radius:4px'
  document.body.appendChild(panel)
  const root = document.documentElement
  window.setInterval(() => {
    const s = (() => {
      try {
        return localStorage.getItem('cards.settings.v1') ?? '(defaults)'
      } catch {
        return '(no storage)'
      }
    })()
    // Written on the screens holding tilt, not on <html> (table/tilt.ts).
    const tiltOn = document.querySelector<HTMLElement>('.play-felt, .hand-band')
    const lines = [
      navigator.userAgent.replace(/\) .*/, ')'),
      `settings ${s}`,
      `html data-flick=${'flick' in root.dataset} data-tilt=${'tilt' in root.dataset} motion=${root.dataset.motion ?? 'system'}` +
        ` reduced=${matchMedia('(prefers-reduced-motion: reduce)').matches}`,
      `tilt vars ${tiltOn?.style.getPropertyValue('--tilt-x') || '-'} ${tiltOn?.style.getPropertyValue('--tilt-y') || '-'}`,
      `hand.flick=${!!document.querySelector('.hand.flick')} drop-slot=${!!document.querySelector('.play-felt .drop-slot')}` +
        ` drop=${document.querySelector<HTMLElement>('.play-felt .trick')?.dataset.drop ?? '-'}`,
      ...Object.entries(values).map(([k, v]) => `${k}: ${v}`),
      Object.entries(counts)
        .map(([k, n]) => `${k}=${n}`)
        .join(' '),
    ]
    panel.textContent = lines.join('\n')
  }, 150)
}
